import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Rocket, Target, Orbit, Database, History, Menu, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import StarfieldBackground from './StarfieldBackground';

const Layout = ({ children }) => {
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);

  const navItems = [
    { path: '/', label: 'Home', icon: Rocket },
    { path: '/predict', label: 'Predict', icon: Target },
    { path: '/orbit', label: 'Orbit Sim', icon: Orbit },
    { path: '/blockchain', label: 'Blockchain', icon: Database },
    { path: '/history', label: 'History', icon: History },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <div className="min-h-screen relative">
      <StarfieldBackground />
      
      {/* Navigation */}
      <nav className="fixed top-0 w-full z-50 glass-card border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <Link to="/" className="flex items-center space-x-2 group" data-testid="logo-link">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-cyan-400 to-purple-600 flex items-center justify-center group-hover:scale-110 transition-transform">
                <Rocket className="w-6 h-6 text-white" />
              </div>
              <span className="text-xl font-bold gradient-text hidden sm:block">AstroMine</span>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden md:flex space-x-1" data-testid="desktop-nav">
              {navItems.map((item) => {
                const Icon = item.icon;
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    data-testid={`nav-${item.label.toLowerCase()}`}
                    className={`
                      px-4 py-2 rounded-lg flex items-center space-x-2
                      transition-all duration-200
                      ${isActive(item.path)
                        ? 'bg-gradient-to-r from-cyan-500/20 to-purple-600/20 text-cyan-400 border border-cyan-500/30'
                        : 'text-gray-300 hover:text-white hover:bg-white/5'
                      }
                    `}
                  >
                    <Icon className="w-4 h-4" />
                    <span className="text-sm font-medium">{item.label}</span>
                  </Link>
                );
              })}
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden p-2 rounded-lg hover:bg-white/5"
              data-testid="mobile-menu-toggle"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        <AnimatePresence>
          {mobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="md:hidden border-t border-white/10"
              data-testid="mobile-nav"
            >
              <div className="px-4 py-4 space-y-2">
                {navItems.map((item) => {
                  const Icon = item.icon;
                  return (
                    <Link
                      key={item.path}
                      to={item.path}
                      onClick={() => setMobileMenuOpen(false)}
                      data-testid={`mobile-nav-${item.label.toLowerCase()}`}
                      className={`
                        flex items-center space-x-3 px-4 py-3 rounded-lg
                        transition-all duration-200
                        ${isActive(item.path)
                          ? 'bg-gradient-to-r from-cyan-500/20 to-purple-600/20 text-cyan-400'
                          : 'text-gray-300 hover:bg-white/5'
                        }
                      `}
                    >
                      <Icon className="w-5 h-5" />
                      <span className="font-medium">{item.label}</span>
                    </Link>
                  );
                })}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>

      {/* Main Content */}
      <main className="pt-16 relative z-10">
        {children}
      </main>

      {/* Footer */}
      <footer className="relative z-10 mt-20 border-t border-white/10 glass-card">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center text-sm text-gray-400">
            <p>© 2025 AstroMine Platform. Powered by ML, Skyfield & Blockchain.</p>
            <p className="mt-2 text-xs">Asteroid mining predictions for the future of space exploration.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Layout;
